import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    ToggleButton,
    ToggleButtonGroup,
    Grid2
} from '@mui/material';
import ProgressSummaryCard from '../components/ProgressSummaryCard';
import ProgressTable from '../components/ProgressTable';
import FrequencyTable from '../components/FrequencyTable';
import {
    fetchAllParticipantsProgress,
    fetchMostFrequentParticipants,
    fetchDashboardStats,
    fetchParticipantDetails,
    fetchParticipantTrends,
    ParticipantProgress,
    DashboardStats,
} from '../api/progress';
import ParticipantProgressPopup from './ParticipantProgressPopup';

const ProgressPage: React.FC = () => {
    const [progressData, setProgressData] = useState<ParticipantProgress[]>([]);
    const [frequencyData, setFrequencyData] = useState<ParticipantProgress[]>([]);
    const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
    const [participantDetails, setParticipantDetails] = useState<any[]>([]);
    const [participantTrends, setParticipantTrends] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingDialog, setLoadingDialog] = useState(true);

    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState<string | null>(null);
    const [timeframe, setTimeframe] = useState<'4weeks' | 'all'>('4weeks');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const days = timeframe === '4weeks' ? 28 : undefined; // 28 days for 4 weeks, undefined for all time
                const [progress, frequency, stats] = await Promise.all([
                    fetchAllParticipantsProgress(days),
                    fetchMostFrequentParticipants(days),
                    fetchDashboardStats(days),
                ]);

                setProgressData(progress);
                setFrequencyData(frequency);
                setDashboardStats(stats);
            } catch (error) {
                console.error('Error fetching progress data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [timeframe]);

    const handleParticipantClick = async (participantId: number, participantName: string) => {
        try {
            setSelectedParticipant(participantName);
            setLoadingDialog(true);
            setDetailModalOpen(true);
            const details = await fetchParticipantDetails(participantId, timeframe === '4weeks' ? 28 : undefined);
            const trends = await fetchParticipantTrends(participantId, timeframe === '4weeks' ? 28 : -1);
            setParticipantDetails(details);
            setParticipantTrends(trends);
        } catch (error) {
            console.error('Error fetching participant details:', error);
        } finally {
            setLoadingDialog(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom color="primary">
                Прогресс и активность
            </Typography>

            {/* Timeframe Selector */}
            <Box display="flex" justifyContent="center" mb={3}>
                <ToggleButtonGroup
                    value={timeframe}
                    exclusive
                    onChange={(event, newTimeframe) => newTimeframe && setTimeframe(newTimeframe)}
                    aria-label="timeframe selector"
                >
                    <ToggleButton value="4weeks" aria-label="last 4 weeks">
                        Последние 4 недели
                    </ToggleButton>
                    <ToggleButton value="all" aria-label="all time">
                        Всё время
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Progress Summary Cards */}
            <Grid2 container spacing={2} mt={2}>
                {/* Group 1: Progress summary */}
                <Grid2 size={12}>
                    <Grid2  container spacing={2} columns={12}>
                        <Grid2  size={{xs:12, sm:6, md:4}}>
                            <ProgressSummaryCard
                                title="Макс. прогресс"
                                value={`${dashboardStats?.maxProgress?.toFixed(0)} %`}
                            />
                        </Grid2>
                        <Grid2 size={{xs:12, sm:6, md:4}}>
                            <ProgressSummaryCard
                                title="Средний прогресс"
                                value={`${dashboardStats?.avgProgress?.toFixed(0)} %`}
                            />
                        </Grid2>
                    </Grid2>
                </Grid2>

                {/* Group 2: Participant summary */}
                <Grid2 size={12}>
                    <Grid2  container columns={12} spacing={2} justifyContent={{ xs: 'center', sm: 'flex-start' }}>
                        <Grid2 size={{xs:12, sm:6, md:4}}>
                            <ProgressSummaryCard
                                title="Самый активный"
                                value={dashboardStats?.mostActiveParticipant?.participant_name ?? ''}
                            />
                        </Grid2>
                        <Grid2 size={{xs:12, sm:6, md:4}}>
                            <ProgressSummaryCard
                                title="Самый прогрессивный"
                                value={dashboardStats?.mostProgressParticipant?.participant_name ?? ''}
                            />
                        </Grid2>
                        <Grid2
                            size={{xs:12, sm:6, md:4}}
                            sx={{
                                margin: {
                                    xs: '0 auto', // Center when on a new row (mobile or small screens)
                                    sm: 'unset', // Default alignment when on the same row
                                },
                            }}
                        >
                            <ProgressSummaryCard
                                title="Самый разносторонний"
                                value={dashboardStats?.mostVersatileParticipant?.participant_name ?? ''}
                            />
                        </Grid2>
                    </Grid2>
                </Grid2>
            </Grid2>

            {/* Progress Table */}
            <Typography variant="h5" mt={4} color="primary">
                Самые прогрессивные участники
            </Typography>
            <ProgressTable
                data={progressData}
                onParticipantClick={(id, name) => handleParticipantClick(id, name)}
            />

            {/* Frequency Section */}
            <Typography variant="h5" mt={4} color="primary">
                Самые активные участники
            </Typography>
            <FrequencyTable
                data={frequencyData}
                onParticipantClick={(id, name) => handleParticipantClick(id, name)}
            />

            {/* Participant Detail Popup */}
            <ParticipantProgressPopup
                detailModalOpen={detailModalOpen}
                setDetailModalOpen={setDetailModalOpen}
                selectedParticipant={selectedParticipant}
                loadingDialog={loadingDialog}
                participantDetails={participantDetails}
                participantTrends={participantTrends}
            />
        </Box>
    );
};

export default ProgressPage;
